import { Link, useRouterState } from '@tanstack/react-router'
import { MessageSquare } from 'lucide-react'
import BetterAuthHeader, {
  ImpersonationBanner,
} from '../integrations/better-auth/header-user.tsx'
import { trackAnalyticsEvent } from '@client/analytics'
import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

type HeaderProps = {
  rightSlot?: ReactNode
}

export default function Header({ rightSlot }: HeaderProps) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const navLinkClassName =
    'flex items-center gap-2 rounded-md px-1.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-2'

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/70 bg-background/85 backdrop-blur-md">
      <a
        href="#main-content"
        className="sr-only absolute left-2 top-2 z-50 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:not-sr-only focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <div className="container mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-8">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-3"
            onClick={() =>
              trackAnalyticsEvent('navigation_clicked', {
                destination: '/',
                label: 'brand',
                location: 'header',
              })
            }
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-foreground text-[11px] font-bold text-background">
              BR
            </div>
            <span className="hidden text-sm font-semibold tracking-tight text-foreground sm:block">
              Building Regs
            </span>
          </Link>

          <nav className="flex min-w-0 items-center gap-1 sm:gap-2">
            <Link
              to="/chat"
              onClick={() =>
                trackAnalyticsEvent('navigation_clicked', {
                  destination: '/chat',
                  label: 'chat',
                  location: 'header',
                })
              }
              className={cn(
                pathname === '/chat' && 'text-foreground',
                navLinkClassName,
              )}
            >
              <MessageSquare size={14} className="text-primary" />
              Chat
            </Link>
            <Link
              to="/pricing"
              onClick={() =>
                trackAnalyticsEvent('navigation_clicked', {
                  destination: '/pricing',
                  label: 'pricing',
                  location: 'header',
                })
              }
              className={cn(
                navLinkClassName,
                pathname === '/pricing' && 'text-foreground',
              )}
            >
              Pricing
            </Link>
          </nav>
        </div>

        <div
          className="flex shrink-0 items-center gap-3 sm:gap-4"
          data-testid="shared-header-actions"
        >
          {rightSlot}
          <BetterAuthHeader />
        </div>
      </div>
      <ImpersonationBanner />
    </header>
  )
}
